import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  XMarkIcon,
  CheckIcon,
  PhotoIcon,
  TrashIcon,
  StarIcon,
  PlusIcon,
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  MinusIcon,
  CubeIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { productsApi, metadataApi, projectsApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import AiGenerateButton from '@/components/AiGenerateButton';
import toast from 'react-hot-toast';
import type {
  VidaProduct,
  ImageWithMetadata,
  EnlaceExterno,
  Disparador,
  TipoProducto,
  PopulatedComponent,
  SimpleProductSummary,
  CollaboratorInfo,
  ContributorProduct,
  PopulatedCollaborator,
  PopulatedContributorProduct,
  OrganizationSummary,
  VisibilityLevel,
} from '@/types/product';
import { TIPO_PRODUCTO, TIPO_PRODUCTO_LABELS, VISIBILITY_LABELS, LANDING_TEMPLATES, LANDING_STYLES } from '@/types/product';
import type { ProductLanding, LandingTemplate } from '@/types/product';

const DEFAULT_LANDING: ProductLanding = {
  active: false,
  template: 'product',
  style: LANDING_STYLES.product[0].id,
  badge: '',
  info_blocks: [],
};

const LANDING_TEMPLATE_LABELS: Record<string, string> = {
  product: 'Producto físico',
  service: 'Servicio',
  corporate: 'Informativa / corporativa',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VariantFormData {
  _id?: string;
  sku: string;
  name: string;
  attributes: Record<string, string>;
  price: number;
  compareAtPrice: number;
  stock: number;
  stockMinimo: number;
  barcode: string;
  weight: number;
  images: string[];
  active: boolean;
}

function makeEmptyVariant(): VariantFormData {
  return {
    sku: '',
    name: '',
    attributes: {},
    price: 0,
    compareAtPrice: 0,
    stock: 0,
    stockMinimo: 0,
    barcode: '',
    weight: 0,
    images: [],
    active: true,
  };
}

interface ProductFormData {
  name: string;
  description: string;
  type: 'simple' | 'composite' | 'collaborative';
  tipoProducto: TipoProducto | '';
  project_id: string;
  parent_id: string;
  price: number;
  descuento: number;
  iva: number;
  pvpFinal: number;
  sku: string;
  stock: number;
  stockMinimo: number;
  alertaStock: boolean;
  parametrosCoste: {
    costeProduccionVariable: number;
    costeProductoFijo: number;
    costeComercialPet: number;
    costeComercialResto: number;
    margen: number;
    descuentos: number;
    precioTemporada: number;
  };
  ecommerce_active: boolean;
  catalog_active: boolean;
  // Tejedores (VIDA-297 / F5): ofrecer este producto a la red de colaboradores.
  shared_with_network: boolean;
  landing: ProductLanding;
  mainImage: string;
  images: ImageWithMetadata[];
  imagenesPorTemporada: Record<string, string>;
  categorization: Record<string, string[]>;
  tags: string[];
  aspectosGenerales: {
    tipoTemporada: string;
    numeroTemporadas: number;
    duracionDias: number;
    pernoctas: number;
    diaEntrada: boolean;
  };
  disponibilidadNegocio: {
    tipoApertura: string;
    diasSemana: Record<string, boolean>;
    mesesAño: Record<string, boolean>;
  };
  marketing: {
    metaSeo: {
      ogTitle: string;
      ogDescription: string;
      metaDescription: string;
      keywords: string;
      targetAudience: string;
      callToAction: string;
    };
    argumentoVenta: string;
    descripcionMarketing: string;
  };
  running: { activo: boolean; descripcionActividad: string };
  gastro: { activo: boolean; tipoServicio: string; logistica: string };
  cupo: {
    activo: boolean;
    tipoLimite: string;
    cantidadMaxima: number;
    fechaLimite: string;
    descripcion: string;
  };
  clienteObjetivo: { tipoCliente: string[]; atributos: string[]; tamaño: string[] };
  estructuraProducto: {
    perspectivaCostes: string;
    margenComercial: string;
    margenIndustrial: string;
    iddi: string;
  };
  enlaces: EnlaceExterno[];
  disparadores: Disparador[];
  // Variants (multi-SKU; attributes free-form key/value)
  variants: VariantFormData[];
  // Composite product fields
  components: Array<{
    product_id: string;
    quantity: number;
    label: string;
    isOptional: boolean;
    priceOverride: number;
  }>;
  minComponents: number;
  maxComponents: number;
  // Collaborative product fields
  collaborativeSettings: {
    isCollaborative: boolean;
    visibilityLevel: VisibilityLevel;
    collaborators: CollaboratorInfo[];
    contributorProducts: ContributorProduct[];
    minCollaborators: number;
    maxCollaborators: number;
  };
}

interface ProductWizardProps {
  product: VidaProduct | null;
  onClose: (saved?: boolean) => void;
}

interface MetadataTree {
  _id: string;
  id: string;
  name: string;
  domain_id: string;
}

interface MetadataNode {
  _id: string;
  id: string;
  name: string;
  parent_id: string | null;
  depth: number;
  path: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Tejedores (VIDA-297 / F5): un colaborador (tejedor) de esta org y los productos
// que ofrece a la red, para elegirlos al montar un producto colaborativo.
interface WeaverProductOption {
  _id: string;
  name: string;
  slug: string;
  price?: number;
  sku?: string;
  mainImage?: string;
}
interface WeaverOption {
  weaver_org_id: string;
  name: string;
  slug: string;
  alias: string;
  products: WeaverProductOption[];
}

const STEPS_SIMPLE = [
  { id: 0, name: 'Información General', icon: '1' },
  { id: 1, name: 'Categorización', icon: '2' },
  { id: 2, name: 'Marketing', icon: '3' },
  { id: 3, name: 'Otros', icon: '4' },
  { id: 4, name: 'Inventario', icon: '5' },
  { id: 5, name: 'Variantes', icon: '6' },
  { id: 6, name: 'Imágenes', icon: '7' },
  { id: 7, name: 'Enlaces', icon: '8' },
  { id: 8, name: 'Resumen', icon: '9' },
];

const STEPS_COMPOSITE = [
  { id: 0, name: 'Información General', icon: '1' },
  { id: 1, name: 'Componentes', icon: '2' },
  { id: 2, name: 'Categorización', icon: '3' },
  { id: 3, name: 'Marketing', icon: '4' },
  { id: 4, name: 'Otros', icon: '5' },
  { id: 5, name: 'Inventario', icon: '6' },
  { id: 6, name: 'Variantes', icon: '7' },
  { id: 7, name: 'Imágenes', icon: '8' },
  { id: 8, name: 'Enlaces', icon: '9' },
  { id: 9, name: 'Resumen', icon: '10' },
];

const STEPS_COLLABORATIVE = [
  { id: 0, name: 'Información General', icon: '1' },
  { id: 1, name: 'Colaboradores', icon: '2' },
  { id: 2, name: 'Productos por Colaborador', icon: '3' },
  { id: 3, name: 'Categorización', icon: '4' },
  { id: 4, name: 'Marketing', icon: '5' },
  { id: 5, name: 'Otros', icon: '6' },
  { id: 6, name: 'Inventario', icon: '7' },
  { id: 7, name: 'Variantes', icon: '8' },
  { id: 8, name: 'Imágenes', icon: '9' },
  { id: 9, name: 'Enlaces', icon: '10' },
  { id: 10, name: 'Resumen', icon: '11' },
];

const DIAS_SEMANA = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDefaultFormData(): ProductFormData {
  return {
    name: '',
    description: '',
    type: 'simple',
    tipoProducto: '',
    project_id: '',
    parent_id: '',
    price: 0,
    descuento: 0,
    iva: 21,
    pvpFinal: 0,
    sku: '',
    stock: 0,
    stockMinimo: 0,
    alertaStock: false,
    parametrosCoste: {
      costeProduccionVariable: 0,
      costeProductoFijo: 0,
      costeComercialPet: 5,
      costeComercialResto: 0,
      margen: 0,
      descuentos: 0,
      precioTemporada: 0,
    },
    ecommerce_active: false,
    shared_with_network: false,
    catalog_active: false,
    landing: { ...DEFAULT_LANDING },
    mainImage: '',
    images: [],
    imagenesPorTemporada: {},
    // Keyed by tree id; entries are added dynamically as the user selects nodes.
    categorization: {},
    tags: [],
    aspectosGenerales: {
      tipoTemporada: '',
      numeroTemporadas: 0,
      duracionDias: 1,
      pernoctas: 0,
      diaEntrada: false,
    },
    disponibilidadNegocio: {
      tipoApertura: 'todo-año',
      diasSemana: Object.fromEntries(DIAS_SEMANA.map((d) => [d, true])),
      mesesAño: Object.fromEntries(MESES.map((m) => [m, true])),
    },
    marketing: {
      metaSeo: {
        ogTitle: '',
        ogDescription: '',
        metaDescription: '',
        keywords: '',
        targetAudience: '',
        callToAction: '',
      },
      argumentoVenta: '',
      descripcionMarketing: '',
    },
    running: { activo: false, descripcionActividad: '' },
    gastro: { activo: false, tipoServicio: '', logistica: '' },
    cupo: { activo: false, tipoLimite: 'cantidad', cantidadMaxima: 0, fechaLimite: '', descripcion: '' },
    clienteObjetivo: { tipoCliente: [], atributos: [], tamaño: [] },
    estructuraProducto: { perspectivaCostes: '', margenComercial: '', margenIndustrial: '', iddi: '' },
    enlaces: [],
    disparadores: [],
    variants: [],
    components: [],
    minComponents: 2,
    maxComponents: 10,
    collaborativeSettings: {
      isCollaborative: false,
      visibilityLevel: 'private',
      collaborators: [],
      contributorProducts: [],
      minCollaborators: 2,
      maxCollaborators: 10,
    },
  };
}

function productToFormData(p: VidaProduct): ProductFormData {
  // Merge each nested block with the defaults instead of falling back to the
  // whole default only when the field is undefined. A product saved before a
  // new sub-field existed will return a partial object (e.g.
  // disponibilidadNegocio: { tipoApertura: 'x' } sin diasSemana), and the
  // old `|| default` form preserved that partial — so accesses like
  // form.disponibilidadNegocio.diasSemana[dia] then threw and crashed the
  // wizard's "Otros" step. Spreading defaults first guarantees every nested
  // field exists; the inner spreads for diasSemana/mesesAño/metaSeo do the
  // same one level deeper.
  const d = getDefaultFormData();
  // Normalizar tipoProducto: si el valor en BD no es un codigo valido del
  // enum (ej: datos legacy con label en lugar de codigo, o seeds que metieron
  // taxonomias distintas), volver a '' para que el wizard pueda guardar sin
  // que el backend rechace por enum. El usuario podra escoger un valor
  // valido del select cuando edite el producto.
  const tipoValido = (TIPO_PRODUCTO as readonly string[]).includes(p.tipoProducto)
    ? (p.tipoProducto as TipoProducto)
    : '';
  return {
    name: p.name,
    description: p.description,
    type: p.type,
    tipoProducto: tipoValido,
    project_id: p.project_id || '',
    parent_id: p.parent_id || '',
    price: p.price,
    descuento: p.descuento,
    iva: p.iva,
    pvpFinal: p.pvpFinal,
    sku: p.sku,
    stock: p.stock,
    stockMinimo: p.stockMinimo,
    alertaStock: p.alertaStock,
    parametrosCoste: { ...d.parametrosCoste, ...(p.parametrosCoste || {}) },
    ecommerce_active: p.ecommerce_active,
    shared_with_network: p.shared_with_network ?? false,
    catalog_active: p.catalog_active,
    landing: { ...DEFAULT_LANDING, ...(p.landing || {}) },
    mainImage: p.mainImage,
    images: p.images || [],
    imagenesPorTemporada: p.imagenesPorTemporada || {},
    categorization: {
      ...d.categorization,
      ...((p.categorization as unknown as Record<string, string[]>) || {}),
    },
    tags: p.tags || [],
    aspectosGenerales: { ...d.aspectosGenerales, ...(p.aspectosGenerales || {}) },
    disponibilidadNegocio: {
      ...d.disponibilidadNegocio,
      ...(p.disponibilidadNegocio || {}),
      diasSemana: {
        ...d.disponibilidadNegocio.diasSemana,
        ...(p.disponibilidadNegocio?.diasSemana || {}),
      },
      mesesAño: {
        ...d.disponibilidadNegocio.mesesAño,
        ...(p.disponibilidadNegocio?.mesesAño || {}),
      },
    },
    marketing: {
      ...d.marketing,
      ...(p.marketing || {}),
      metaSeo: {
        ...d.marketing.metaSeo,
        ...((p.marketing as { metaSeo?: typeof d.marketing.metaSeo })?.metaSeo || {}),
      },
    },
    running: { ...d.running, ...(p.running || {}) },
    gastro: { ...d.gastro, ...(p.gastro || {}) },
    cupo: { ...d.cupo, ...(p.cupo || {}) },
    clienteObjetivo: { ...d.clienteObjetivo, ...(p.clienteObjetivo || {}) },
    estructuraProducto: { ...d.estructuraProducto, ...(p.estructuraProducto || {}) },
    enlaces: p.enlaces || [],
    disparadores: p.disparadores || [],
    variants: ((p as VidaProduct & { variants?: Array<Partial<VariantFormData>> }).variants || []).map((v) => ({
      _id: v._id,
      sku: v.sku || '',
      name: v.name || '',
      attributes: v.attributes || {},
      price: v.price || 0,
      compareAtPrice: v.compareAtPrice || 0,
      stock: v.stock || 0,
      stockMinimo: v.stockMinimo || 0,
      barcode: v.barcode || '',
      weight: v.weight || 0,
      images: v.images || [],
      active: v.active !== false,
    })),
    components: (p.components || []).map((c) => ({
      product_id: c.product_id,
      quantity: c.quantity,
      label: c.label,
      isOptional: c.isOptional,
      priceOverride: c.priceOverride,
    })),
    minComponents: p.minComponents || 2,
    maxComponents: p.maxComponents || 10,
    collaborativeSettings: p.collaborativeSettings || getDefaultFormData().collaborativeSettings,
  };
}

function calcPvpFinal(price: number, descuento: number, iva: number): number {
  const priceAfterDiscount = price * (1 - descuento / 100);
  return Math.round(priceAfterDiscount * (1 + iva / 100) * 100) / 100;
}

// ---------------------------------------------------------------------------
// Sub-component: Inline product search for a specific collaborator
// ---------------------------------------------------------------------------

function CollaboratorProductSearch({
  existingProductIds,
  onAdd,
}: {
  orgId: string;
  existingProductIds: string[];
  onAdd: (product: SimpleProductSummary) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SimpleProductSummary[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (query.length < 1) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const res = await productsApi.searchSimpleProducts(query, existingProductIds);
        setResults(res.data.products || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, existingProductIds]);

  return (
    <div>
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar productos simples para añadir..."
          className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-teal-500"
        />
      </div>
      {searching && <p className="text-xs text-gray-400 mt-1">Buscando...</p>}
      {results.length > 0 && (
        <div className="mt-1 border border-gray-200 rounded max-h-40 overflow-y-auto divide-y divide-gray-100">
          {results.map((p) => (
            <div key={p._id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 text-xs">
              <span className="flex-1 truncate">{p.name}</span>
              <span className="text-gray-500">{p.price.toFixed(2)} €</span>
              <button
                onClick={() => {
                  onAdd(p);
                  setResults((prev) => prev.filter((r) => r._id !== p._id));
                }}
                className="text-teal-600 hover:text-teal-800 font-medium"
              >
                + Añadir
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Generate a unique-ish id for client-side ephemeral entities (image entries,
 * enlaces, etc.). Prefers crypto.randomUUID() but falls back to Date+Math
 * because randomUUID() requires a "secure context" (HTTPS or localhost) and
 * the dashboard is currently served over HTTP on a public IP, where it throws.
 */
function makeUid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      // ignore — fall through to fallback
    }
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function ProductWizard({ product, onClose }: ProductWizardProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<ProductFormData>(
    product ? productToFormData(product) : getDefaultFormData()
  );
  const [productId, setProductId] = useState<string | null>(product?._id || null);
  const [saving, setSaving] = useState(false);
  // Slug de la organización activa: hace falta para decirle al usuario los dos
  // valores que necesita el despliegue de la landing.
  const orgSlug = useOrganizationStore((s) => s.selectedOrg?.slug) || '';
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyValue = (field: string, value: string) => {
    navigator.clipboard?.writeText(value).then(
      () => {
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 1500);
      },
      () => { /* portapapeles no disponible: no rompemos nada */ }
    );
  };
  const [metadataTrees, setMetadataTrees] = useState<MetadataTree[]>([]);
  const [treeNodes, setTreeNodes] = useState<Record<string, MetadataNode[]>>({});

  // Composite product state
  const [componentSearch, setComponentSearch] = useState('');
  const [searchResults, setSearchResults] = useState<SimpleProductSummary[]>([]);
  const [searchingComponents, setSearchingComponents] = useState(false);
  const [populatedComponents, setPopulatedComponents] = useState<PopulatedComponent[]>([]);

  // Collaborative product state
  const [orgSearch, setOrgSearch] = useState('');
  const [orgSearchResults, setOrgSearchResults] = useState<OrganizationSummary[]>([]);
  // Tejedores colaboradores de esta org + sus productos ofrecidos a la red (F5).
  const [weaverOptions, setWeaverOptions] = useState<WeaverOption[]>([]);
  const [searchingOrgs, setSearchingOrgs] = useState(false);
  const [populatedCollaborators, setPopulatedCollaborators] = useState<PopulatedCollaborator[]>([]);
  const [populatedContributorProducts, setPopulatedContributorProducts] = useState<PopulatedContributorProduct[]>([]);

  // External user node restrictions
  const [allowedNodeIds, setAllowedNodeIds] = useState<Set<string> | null>(null);
  const { user } = useAuthStore();
  const isExternalUser = user?.user_type === 'external';

  // Projects for project selector
  const [projects, setProjects] = useState<{ _id: string; name: string; status: string }[]>([]);
  const [parentProducts, setParentProducts] = useState<{ _id: string; name: string }[]>([]);

  const isEditing = !!product;
  const isComposite = form.type === 'composite';
  const isCollaborative = form.type === 'collaborative';
  const STEPS = isCollaborative ? STEPS_COLLABORATIVE : isComposite ? STEPS_COMPOSITE : STEPS_SIMPLE;

  // Load allowed nodes for external users
  useEffect(() => {
    if (!isExternalUser) return;
    const loadMyNodes = async () => {
      try {
        const res = await projectsApi.getMyNodes();
        const data = res.data;
        if (data.restricted && data.allowed_nodes) {
          setAllowedNodeIds(new Set(data.allowed_nodes));
        }
      } catch {
        // Non-critical - will show all nodes if can't load restrictions
      }
    };
    loadMyNodes();
  }, [isExternalUser]);

  // Auto-calc PVP Final
  useEffect(() => {
    const pvp = calcPvpFinal(form.price, form.descuento, form.iva);
    if (pvp !== form.pvpFinal) {
      setForm((prev) => ({ ...prev, pvpFinal: pvp }));
    }
  }, [form.price, form.descuento, form.iva]);

  // Load metadata trees for categorization step
  useEffect(() => {
    const loadTrees = async () => {
      try {
        // forProducts → only the trees configured for this org's product editor.
        const res = await metadataApi.getTrees(undefined, true);
        setMetadataTrees(res.data.data || res.data.trees || []);
      } catch {
        // Trees might not be configured yet
      }
    };
    loadTrees();
  }, []);

  // Eager-load nodes for every tree so the categorization step shows them without
  // a manual click (trees arrive already scoped to the org from GET /metadata/trees).
  useEffect(() => {
    metadataTrees.forEach((t) => loadNodesForTree(t._id));
    // loadNodesForTree is memoised and no-ops when nodes are already loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataTrees]);

  // Load projects for project selector
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await projectsApi.list(1, 100, { status: 'active' });
        setProjects(res.data.projects || []);
      } catch {
        // Projects might not be configured yet
      }
    };
    loadProjects();
  }, []);

  // Load potential parent products (top-level only, exclude self)
  useEffect(() => {
    const loadParents = async () => {
      try {
        const res = await productsApi.getProducts(1, 100, { parent_id: 'none' });
        const all = res.data.products || [];
        setParentProducts(
          all.filter((p: { _id: string }) => !product || p._id !== product._id).map((p: { _id: string; name: string }) => ({ _id: p._id, name: p.name }))
        );
      } catch { /* non-critical */ }
    };
    loadParents();
  }, [product]);

  const loadNodesForTree = useCallback(
    async (treeId: string) => {
      if (treeNodes[treeId]) return;
      try {
        const res = await metadataApi.getNodes(treeId);
        // Endpoint returns { success, data: [...] }; keep older shapes as fallback
        // but always store an ARRAY (a non-array here crashes the .map render).
        const payload = res.data?.data ?? res.data?.nodes ?? res.data;
        setTreeNodes((prev) => ({
          ...prev,
          [treeId]: Array.isArray(payload) ? payload : [],
        }));
      } catch {
        // Non-critical
      }
    },
    [treeNodes]
  );

  // Load populated components when editing a composite product
  useEffect(() => {
    if (isComposite && productId && form.components.length > 0) {
      const loadComponents = async () => {
        try {
          const res = await productsApi.getComponents(productId);
          setPopulatedComponents(res.data.components || []);
        } catch {
          // Non-critical
        }
      };
      loadComponents();
    }
  }, [isComposite, productId]);

  // Search simple products for composite components
  const searchSimpleProducts = useCallback(
    async (query: string) => {
      setSearchingComponents(true);
      try {
        const excludeIds = form.components.map((c) => c.product_id);
        // Also exclude self if editing
        if (productId) excludeIds.push(productId);
        const res = await productsApi.searchSimpleProducts(query, excludeIds);
        setSearchResults(res.data.products || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchingComponents(false);
      }
    },
    [form.components, productId]
  );

  // Debounced search
  useEffect(() => {
    if (!isComposite) return;
    const timeout = setTimeout(() => {
      if (componentSearch.length >= 1) {
        searchSimpleProducts(componentSearch);
      } else {
        searchSimpleProducts('');
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [componentSearch, isComposite, searchSimpleProducts]);

  // Load populated collaborative data when editing a collaborative product
  useEffect(() => {
    if (isCollaborative && productId) {
      const loadCollaborativeData = async () => {
        try {
          const res = await productsApi.getCollaborativeView(productId);
          setPopulatedCollaborators(res.data.collaborators || []);
          setPopulatedContributorProducts(res.data.contributorProducts || []);
        } catch {
          // Non-critical
        }
      };
      loadCollaborativeData();
    }
  }, [isCollaborative, productId]);

  // Search organizations for collaborative products
  const searchOrganizations = useCallback(async (query: string) => {
    setSearchingOrgs(true);
    try {
      const res = await productsApi.searchOrganizations(query);
      setOrgSearchResults(res.data.organizations || []);
    } catch {
      setOrgSearchResults([]);
    } finally {
      setSearchingOrgs(false);
    }
  }, []);

  // Cargar los tejedores colaboradores de la org y sus productos ofrecidos (F5).
  useEffect(() => {
    if (!isCollaborative) return;
    let alive = true;
    productsApi
      .collaboratorsOptions()
      .then((res) => { if (alive) setWeaverOptions(res.data.collaborators || []); })
      .catch(() => { if (alive) setWeaverOptions([]); });
    return () => { alive = false; };
  }, [isCollaborative]);

  // Debounced org search
  useEffect(() => {
    if (!isCollaborative) return;
    const timeout = setTimeout(() => {
      searchOrganizations(orgSearch);
    }, 300);
    return () => clearTimeout(timeout);
  }, [orgSearch, isCollaborative, searchOrganizations]);

  // Add a collaborator
  const addCollaborator = (org: OrganizationSummary) => {
    const cs = form.collaborativeSettings;
    if (cs.collaborators.length >= cs.maxCollaborators) {
      toast.error(`Máximo ${cs.maxCollaborators} colaboradores`);
      return;
    }
    if (cs.collaborators.some((c) => c.organization_id === org._id)) {
      toast.error('Esta organización ya es colaboradora');
      return;
    }
    const newCollab: CollaboratorInfo = {
      organization_id: org._id,
      role: 'editor',
      contribution: 0,
      label: org.name,
    };
    updateField('collaborativeSettings', {
      ...cs,
      isCollaborative: true,
      collaborators: [...cs.collaborators, newCollab],
    });
    setPopulatedCollaborators((prev) => [
      ...prev,
      { ...newCollab, orgName: org.name, orgSlug: org.slug },
    ]);
    setOrgSearchResults((prev) => prev.filter((o) => o._id !== org._id));
  };

  // Remove a collaborator
  const removeCollaborator = (orgId: string) => {
    const cs = form.collaborativeSettings;
    updateField('collaborativeSettings', {
      ...cs,
      collaborators: cs.collaborators.filter((c) => c.organization_id !== orgId),
      contributorProducts: cs.contributorProducts.filter((cp) => cp.organization_id !== orgId),
    });
    setPopulatedCollaborators((prev) => prev.filter((c) => c.organization_id !== orgId));
    setPopulatedContributorProducts((prev) => prev.filter((cp) => cp.organization_id !== orgId));
  };

  // Update collaborator contribution
  const updateContribution = (orgId: string, contribution: number) => {
    const cs = form.collaborativeSettings;
    updateField('collaborativeSettings', {
      ...cs,
      collaborators: cs.collaborators.map((c) =>
        c.organization_id === orgId ? { ...c, contribution } : c
      ),
    });
    setPopulatedCollaborators((prev) =>
      prev.map((c) => (c.organization_id === orgId ? { ...c, contribution } : c))
    );
  };

  // Update collaborator role
  const updateCollaboratorRole = (orgId: string, role: 'owner' | 'editor') => {
    const cs = form.collaborativeSettings;
    updateField('collaborativeSettings', {
      ...cs,
      collaborators: cs.collaborators.map((c) =>
        c.organization_id === orgId ? { ...c, role } : c
      ),
    });
    setPopulatedCollaborators((prev) =>
      prev.map((c) => (c.organization_id === orgId ? { ...c, role } : c))
    );
  };

  // Add contributor product (from search results, associated with a collaborator org)
  const addContributorProduct = (product: SimpleProductSummary, orgId: string) => {
    const cs = form.collaborativeSettings;
    const newCp: ContributorProduct = {
      product_id: product._id,
      organization_id: orgId,
      quantity: 1,
      label: product.name,
    };
    updateField('collaborativeSettings', {
      ...cs,
      contributorProducts: [...cs.contributorProducts, newCp],
    });
    setPopulatedContributorProducts((prev) => [
      ...prev,
      {
        ...newCp,
        orgName: populatedCollaborators.find((c) => c.organization_id === orgId)?.orgName || '',
        source: {
          _id: product._id,
          name: product.name,
          price: product.price,
          pvpFinal: product.pvpFinal,
          sku: product.sku,
          mainImage: product.mainImage,
        },
      },
    ]);
  };

  // Remove contributor product
  const removeContributorProduct = (productId: string, orgId: string) => {
    const cs = form.collaborativeSettings;
    updateField('collaborativeSettings', {
      ...cs,
      contributorProducts: cs.contributorProducts.filter(
        (cp) => !(cp.product_id === productId && cp.organization_id === orgId)
      ),
    });
    setPopulatedContributorProducts((prev) =>
      prev.filter((cp) => !(cp.product_id === productId && cp.organization_id === orgId))
    );
  };

  // Add a component
  const addComponent = (product: SimpleProductSummary) => {
    if (form.components.length >= form.maxComponents) {
      toast.error(`Máximo ${form.maxComponents} componentes`);
      return;
    }
    const newComp = {
      product_id: product._id,
      quantity: 1,
      label: product.name,
      isOptional: false,
      priceOverride: -1,
    };
    updateField('components', [...form.components, newComp]);
    // Add to populated view
    setPopulatedComponents((prev) => [
      ...prev,
      {
        ...newComp,
        effectivePrice: product.price,
        subtotal: product.price,
        source: {
          _id: product._id,
          name: product.name,
          price: product.price,
          pvpFinal: product.pvpFinal,
          sku: product.sku,
          mainImage: product.mainImage,
          status: product.status,
          tipoProducto: product.tipoProducto,
        },
      },
    ]);
    // Remove from search results
    setSearchResults((prev) => prev.filter((p) => p._id !== product._id));
  };

  // Remove a component
  const removeComponent = (productId: string) => {
    updateField(
      'components',
      form.components.filter((c) => c.product_id !== productId)
    );
    setPopulatedComponents((prev) => prev.filter((c) => c.product_id !== productId));
  };

  // Update component quantity
  const updateComponentQuantity = (productId: string, quantity: number) => {
    if (quantity < 1) return;
    updateField(
      'components',
      form.components.map((c) =>
        c.product_id === productId ? { ...c, quantity } : c
      )
    );
    setPopulatedComponents((prev) =>
      prev.map((c) => {
        if (c.product_id !== productId) return c;
        const effectivePrice = c.priceOverride >= 0 ? c.priceOverride : (c.source?.price || 0);
        return { ...c, quantity, subtotal: effectivePrice * quantity };
      })
    );
  };

  // Toggle component optional
  const toggleComponentOptional = (productId: string) => {
    updateField(
      'components',
      form.components.map((c) =>
        c.product_id === productId ? { ...c, isOptional: !c.isOptional } : c
      )
    );
    setPopulatedComponents((prev) =>
      prev.map((c) =>
        c.product_id === productId ? { ...c, isOptional: !c.isOptional } : c
      )
    );
  };

  // Helper: update form field
  const updateField = <K extends keyof ProductFormData>(key: K, value: ProductFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // --- Save Logic ---

  // `silent` se usa en el auto-save al cambiar de tab: el guardado sucede
  // igual, pero sin toast de exito para no spamear al usuario en cada
  // navegacion. Solo se muestra cuando el usuario clickea Guardar/Publicar
  // explicitamente.
  const saveProduct = async (publish = false, silent = false) => {
    if (!form.name.trim()) {
      if (!silent) toast.error('El nombre del producto es obligatorio');
      return;
    }
    if (isExternalUser && !form.project_id) {
      if (!silent) toast.error('Debes seleccionar un proyecto');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (publish) {
        (payload as Record<string, unknown>).status = 'active';
      }

      if (productId) {
        await productsApi.updateProduct(productId, payload);
        if (publish) {
          await productsApi.changeStatus(productId, 'active');
        }
        if (!silent) toast.success('Producto actualizado');
      } else {
        const res = await productsApi.createProduct(payload);
        setProductId(res.data.product._id);
        if (publish) {
          await productsApi.changeStatus(res.data.product._id, 'active');
        }
        if (!silent) toast.success('Producto creado');
      }
    } catch (err: unknown) {
      let msg = 'Error al guardar';
      if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response: { data: { error?: string; details?: unknown } } }).response?.data;
        msg = data?.error ?? msg;
        // Validation zod: incluir el primer field que falla para que el bug
        // se diagnostique sin abrir DevTools.
        if (Array.isArray(data?.details) && data.details.length > 0) {
          const first = data.details[0] as { path?: (string | number)[]; message?: string };
          if (first.path && first.message) {
            msg += `: ${first.path.join('.')} — ${first.message}`;
          }
        }
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Auto-save when changing steps (if product has a name). Silent: el
  // guardado pasa pero no se muestra toast de exito — el usuario solo
  // recibe feedback cuando clickea Guardar/Publicar explicitamente, y
  // los errores del backend se siguen mostrando.
  const goToStep = async (step: number) => {
    if (form.name.trim() && step !== activeStep) {
      await saveProduct(false, true);
    }
    setActiveStep(step);
  };

  // --- Image Upload ---

  const handleImageUpload = async (files: FileList) => {
    if (!productId) {
      // Need to save the product first
      if (!form.name.trim()) {
        toast.error('Guarda el producto primero (pon un nombre)');
        return;
      }
      await saveProduct();
    }

    const currentProductId = productId;
    if (!currentProductId) return;

    for (const file of Array.from(files)) {
      try {
        // Get presigned URL
        const urlRes = await productsApi.getUploadUrl(currentProductId, file.name);
        const { uploadUrl, objectKey, publicUrl } = urlRes.data;

        // Upload to MinIO
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        if (!putRes.ok) {
          const text = await putRes.text().catch(() => '');
          throw new Error(`Storage returned ${putRes.status}: ${text.substring(0, 200)}`);
        }

        // Add to images array.
        const newImage: ImageWithMetadata = {
          id: makeUid(),
          src: objectKey,
          url: publicUrl,
          etiqueta: '',
          referencia: '',
          orden: form.images.length,
          enlace: '',
          tipo: 'archivo',
          temporada: 'general',
        };

        setForm((prev) => {
          const images = [...prev.images, newImage];
          const mainImage = prev.mainImage || publicUrl;
          return { ...prev, images, mainImage };
        });

        toast.success(`Imagen ${file.name} subida`);
      } catch (err: unknown) {
        // Surface the real error so we can debug instead of swallowing it.
        // eslint-disable-next-line no-console
        console.error('[ProductWizard] image upload failed:', err);
        const msg = err instanceof Error ? err.message : 'unknown';
        toast.error(`Error subiendo ${file.name}: ${msg}`);
      }
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    if (!productId) return;
    try {
      await productsApi.deleteImage(productId, imageId);
      setForm((prev) => {
        const images = prev.images.filter((img) => img.id !== imageId);
        const deletedImg = prev.images.find((img) => img.id === imageId);
        let mainImage = prev.mainImage;
        if (deletedImg && (mainImage === deletedImg.url || mainImage === deletedImg.src)) {
          mainImage = images[0]?.url || '';
        }
        return { ...prev, images, mainImage };
      });
      toast.success('Imagen eliminada');
    } catch {
      toast.error('Error al eliminar imagen');
    }
  };

  const setMainImage = async (url: string) => {
    // Optimistic local update so the UI updates instantly even before the PATCH
    // resolves (avoids the star flicker on a slow connection).
    updateField('mainImage', url);

    // If the product is already saved, persist the change immediately so the
    // user doesn't have to navigate to Resumen + Guardar just to switch which
    // photo is the featured one. If the product hasn't been saved yet, the
    // local form state is enough — it'll be persisted by the next save.
    if (!productId) return;
    try {
      await productsApi.updateProduct(productId, { mainImage: url });
      toast.success('Imagen destacada actualizada');
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Error al actualizar la imagen destacada';
      toast.error(message);
    }
  };

  // --- Enlace helpers ---

  const addEnlace = () => {
    const newEnlace: EnlaceExterno = {
      id: makeUid(),
      tipo: 'tienda',
      url: '',
      activo: true,
    };
    updateField('enlaces', [...form.enlaces, newEnlace]);
  };

  const removeEnlace = (id: string) => {
    updateField(
      'enlaces',
      form.enlaces.filter((e) => e.id !== id)
    );
  };

  const updateEnlace = (id: string, field: string, value: unknown) => {
    updateField(
      'enlaces',
      form.enlaces.map((e) => (e.id === id ? { ...e, [field]: value } : e))
    );
  };

  // ---------------------------------------------------------------------------
  // Step Renderers
  // ---------------------------------------------------------------------------

  const renderStep0_InfoGeneral = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Información General</h3>

      {/* Categoría y subcategoría del árbol Comercial de la organización.
          Se muestran AQUÍ, y no solo en la pestaña Categorización, porque son
          los dos campos que todo el mundo busca al dar de alta un producto:
          esconderlos tres pestañas más allá hace que se queden sin rellenar.

          Escriben en el mismo `form.categorization[tree.id]` que la pestaña de
          Categorización, así que ambas vistas van siempre sincronizadas.

          Los ejes son las RAMAS RAÍZ del árbol y son INDEPENDIENTES: elegir
          categoría no limita la subcategoría. En la Asociación de Guadix son el
          paisaje y el tipo de actividad, y un mismo tipo —vuelos en globo— se
          da en varios paisajes. */}
      {(() => {
        const arbol = metadataTrees.find((t) => /comercial/i.test(t.id));
        if (!arbol) return null;
        const nodos = treeNodes[arbol._id] || [];
        if (nodos.length === 0) return null;

        const raices = nodos.filter((n) => !n.parent_id);
        if (raices.length === 0) return null;

        const key = arbol.id;
        const seleccion = Array.isArray(form.categorization[key]) ? form.categorization[key] : [];

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-vida-50 border border-vida-100 rounded-lg">
            {raices.map((raiz) => {
              const hijos = nodos.filter((n) => n.parent_id === raiz._id);
              if (hijos.length === 0) return null;
              const idsDelEje = new Set(hijos.map((h) => h._id));
              const elegido = seleccion.find((id) => idsDelEje.has(id)) || '';

              return (
                <div key={raiz._id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{raiz.name}</label>
                  <select
                    value={elegido}
                    onChange={(e) => {
                      // Se reemplaza solo la eleccion de ESTE eje y se respetan
                      // las de los demas, que viven en la misma lista.
                      const resto = seleccion.filter((id) => !idsDelEje.has(id));
                      updateField('categorization', {
                        ...form.categorization,
                        [key]: e.target.value ? [...resto, e.target.value] : resto,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vida-500"
                  >
                    <option value="">— Sin asignar —</option>
                    {hijos.map((h) => (
                      <option key={h._id} value={h._id}>
                        {h.name}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
            <p className="md:col-span-2 text-xs text-gray-500">
              Determinan dónde aparece el producto en la tienda y el catálogo. Se pueden
              afinar, y añadir varios por eje, en la pestaña Categorización.
            </p>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vida-500"
            placeholder="Nombre del producto"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
          <textarea
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vida-500"
            placeholder="Descripción del producto"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Producto</label>
          <select
            value={form.type}
            onChange={(e) => updateField('type', e.target.value as 'simple' | 'composite' | 'collaborative')}
            className="w-full px-3 py-2 border-2 border-vida-300 bg-vida-50 rounded-lg font-medium text-vida-800 focus:ring-2 focus:ring-vida-500"
            disabled={isEditing}
          >
            <option value="simple">Simple</option>
            <option value="composite">Compuesto</option>
            <option value="collaborative">Colaborativo</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Clasificación</label>
          <select
            value={form.tipoProducto}
            onChange={(e) => updateField('tipoProducto', e.target.value as TipoProducto)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">— Seleccionar —</option>
            {Object.entries(TIPO_PRODUCTO_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {projects.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proyecto {isExternalUser && <span className="text-red-500">*</span>}
            </label>
            <select
              value={form.project_id}
              onChange={(e) => updateField('project_id', e.target.value)}
              className="w-full px-3 py-2 border-2 border-vida-300 bg-vida-50 rounded-lg font-medium text-vida-800 focus:ring-2 focus:ring-vida-500"
              required={isExternalUser}
            >
              {!isExternalUser && <option value="">— Sin proyecto —</option>}
              {projects.map((proj) => (
                <option key={proj._id} value={proj._id}>
                  {proj.name}
                </option>
              ))}
            </select>
            {isExternalUser && !form.project_id && (
              <p className="mt-1 text-xs text-red-500">Debes seleccionar un proyecto</p>
            )}
          </div>
        )}
      </div>

      {/* Parent product (hierarchy for catalog) */}
      {parentProducts.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Producto padre (jerarquia catalogo)
          </label>
          <select
            value={form.parent_id}
            onChange={(e) => updateField('parent_id', e.target.value)}
            className="w-full px-3 py-2 border-2 border-vida-300 bg-vida-50 rounded-lg font-medium text-vida-800 focus:ring-2 focus:ring-vida-500"
          >
            <option value="">— Producto raiz (nivel superior) —</option>
            {parentProducts.map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">Si seleccionas un padre, este producto aparecera como sub-producto en el catalogo</p>
        </div>
      )}

      {/* Pricing */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Precios</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Precio base (€)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={form.price}
              onChange={(e) => updateField('price', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Descuento (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={form.descuento}
              onChange={(e) => updateField('descuento', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">IVA (%)</label>
            <input
              type="number"
              min={0}
              value={form.iva}
              onChange={(e) => updateField('iva', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">PVP Final (€)</label>
            <input
              type="number"
              value={form.pvpFinal}
              readOnly
              className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg font-semibold text-green-700"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
        <input
          type="text"
          value={form.sku}
          onChange={(e) => updateField('sku', e.target.value.toUpperCase())}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          placeholder="Código de producto"
        />
      </div>

      {/* Parámetros de coste */}
      <details className="border border-gray-200 rounded-lg p-4">
        <summary className="cursor-pointer text-sm font-semibold text-gray-700">
          Parámetros de Coste (C1-C7)
        </summary>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { key: 'costeProduccionVariable', label: 'C1 Producción Variable' },
            { key: 'costeProductoFijo', label: 'C2 Producto Fijo' },
            { key: 'costeComercialPet', label: 'C3 Comercial VIDA (5%)' },
            { key: 'costeComercialResto', label: 'C4 Comercial Resto' },
            { key: 'margen', label: 'C5 Margen' },
            { key: 'descuentos', label: 'C6 Descuentos' },
            { key: 'precioTemporada', label: 'C7 Temporada' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs text-gray-500 mb-1">{label}</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={(form.parametrosCoste as Record<string, number>)[key] || 0}
                onChange={(e) =>
                  updateField('parametrosCoste', {
                    ...form.parametrosCoste,
                    [key]: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
          ))}
        </div>
      </details>
    </div>
  );

  // --- Composite: Components Step ---

  const totalComponentsPrice = populatedComponents
    .filter((c) => !c.isOptional)
    .reduce((sum, c) => sum + c.subtotal, 0);

  const renderStepComponents = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Componentes del Producto Compuesto</h3>
      <p className="text-sm text-gray-500">
        Selecciona los productos simples que forman este producto compuesto
        (mín. {form.minComponents}, máx. {form.maxComponents}).
      </p>

      {/* Min/Max config */}
      <div className="flex gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Mín. componentes</label>
          <input
            type="number"
            min={2}
            max={form.maxComponents}
            value={form.minComponents}
            onChange={(e) => updateField('minComponents', Math.max(2, parseInt(e.target.value) || 2))}
            className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Máx. componentes</label>
          <input
            type="number"
            min={form.minComponents}
            max={50}
            value={form.maxComponents}
            onChange={(e) => updateField('maxComponents', Math.min(50, parseInt(e.target.value) || 10))}
            className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
        </div>
      </div>

      {/* Current components */}
      {populatedComponents.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700">
              Componentes seleccionados ({populatedComponents.length})
            </h4>
            <span className="text-sm font-semibold text-green-700">
              Total: {totalComponentsPrice.toFixed(2)} €
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {populatedComponents.map((comp) => (
              <div
                key={comp.product_id}
                className={`flex items-center gap-3 px-4 py-3 ${comp.isOptional ? 'bg-yellow-50' : ''}`}
              >
                {comp.source?.mainImage ? (
                  <img
                    src={comp.source.mainImage}
                    alt={comp.label}
                    className="h-10 w-10 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <CubeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{comp.label}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    {comp.source?.sku && <span>SKU: {comp.source.sku}</span>}
                    <span>{comp.effectivePrice.toFixed(2)} € / ud.</span>
                    {comp.isOptional && (
                      <span className="text-yellow-600 font-medium">Opcional</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Quantity */}
                  <div className="flex items-center border border-gray-300 rounded">
                    <button
                      onClick={() => updateComponentQuantity(comp.product_id, comp.quantity - 1)}
                      disabled={comp.quantity <= 1}
                      className="px-2 py-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                    >
                      <MinusIcon className="h-3 w-3" />
                    </button>
                    <span className="px-2 text-sm font-medium min-w-[2rem] text-center">
                      {comp.quantity}
                    </span>
                    <button
                      onClick={() => updateComponentQuantity(comp.product_id, comp.quantity + 1)}
                      className="px-2 py-1 text-gray-500 hover:bg-gray-100"
                    >
                      <PlusIcon className="h-3 w-3" />
                    </button>
                  </div>

                  {/* Subtotal */}
                  <span className="text-sm font-semibold text-gray-700 w-20 text-right">
                    {comp.subtotal.toFixed(2)} €
                  </span>

                  {/* Optional toggle */}
                  <button
                    onClick={() => toggleComponentOptional(comp.product_id)}
                    className={`px-2 py-1 text-xs rounded border ${
                      comp.isOptional
                        ? 'bg-yellow-100 text-yellow-700 border-yellow-300'
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}
                    title={comp.isOptional ? 'Marcar como obligatorio' : 'Marcar como opcional'}
                  >
                    {comp.isOptional ? 'Opcional' : 'Obligat.'}
                  </button>

                  {/* Remove */}
                  <button
                    onClick={() => removeComponent(comp.product_id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validation message */}
      {populatedComponents.length < form.minComponents && (
        <p className="text-sm text-amber-600">
          Necesitas al menos {form.minComponents} componentes. Tienes {populatedComponents.length}.
        </p>
      )}

      {/* Search & add components */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Buscar productos simples</h4>
        <div className="relative mb-3">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={componentSearch}
            onChange={(e) => setComponentSearch(e.target.value)}
            placeholder="Buscar por nombre..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-vida-500"
          />
        </div>

        {searchingComponents ? (
          <p className="text-sm text-gray-400 py-4 text-center">Buscando...</p>
        ) : searchResults.length > 0 ? (
          <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
            {searchResults.map((sp) => (
              <div
                key={sp._id}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50"
              >
                {sp.mainImage ? (
                  <img
                    src={sp.mainImage}
                    alt={sp.name}
                    className="h-8 w-8 rounded object-cover"
                  />
                ) : (
                  <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                    <CubeIcon className="h-4 w-4 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{sp.name}</p>
                  <p className="text-xs text-gray-500">
                    {sp.price.toFixed(2)} €{sp.sku ? ` · SKU: ${sp.sku}` : ''}
                  </p>
                </div>
                <button
                  onClick={() => addComponent(sp)}
                  disabled={form.components.length >= form.maxComponents}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 disabled:opacity-50"
                >
                  <PlusIcon className="h-3 w-3" />
                  Añadir
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-4 text-center">
            {componentSearch ? 'No se encontraron productos simples' : 'Escribe para buscar productos'}
          </p>
        )}
      </div>
    </div>
  );

  // --- Collaborative: Collaborators Step ---

  const totalContribution = form.collaborativeSettings.collaborators.reduce(
    (sum, c) => sum + c.contribution, 0
  );

  const renderStepCollaborators = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Colaboradores</h3>
      <p className="text-sm text-gray-500">
        Selecciona las organizaciones que participan en este producto colaborativo
        (mín. {form.collaborativeSettings.minCollaborators}, máx. {form.collaborativeSettings.maxCollaborators}).
      </p>

      {/* Visibility level */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nivel de Visibilidad</label>
        <select
          value={form.collaborativeSettings.visibilityLevel}
          onChange={(e) =>
            updateField('collaborativeSettings', {
              ...form.collaborativeSettings,
              visibilityLevel: e.target.value as VisibilityLevel,
            })
          }
          className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg"
        >
          {Object.entries(VISIBILITY_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Min/Max config */}
      <div className="flex gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Mín. colaboradores</label>
          <input
            type="number"
            min={2}
            max={form.collaborativeSettings.maxCollaborators}
            value={form.collaborativeSettings.minCollaborators}
            onChange={(e) =>
              updateField('collaborativeSettings', {
                ...form.collaborativeSettings,
                minCollaborators: Math.max(2, parseInt(e.target.value) || 2),
              })
            }
            className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Máx. colaboradores</label>
          <input
            type="number"
            min={form.collaborativeSettings.minCollaborators}
            max={50}
            value={form.collaborativeSettings.maxCollaborators}
            onChange={(e) =>
              updateField('collaborativeSettings', {
                ...form.collaborativeSettings,
                maxCollaborators: Math.min(50, parseInt(e.target.value) || 10),
              })
            }
            className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
        </div>
      </div>

      {/* Current collaborators */}
      {populatedCollaborators.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700">
              Colaboradores seleccionados ({populatedCollaborators.length})
            </h4>
            <span className={`text-sm font-semibold ${totalContribution === 100 ? 'text-green-700' : 'text-amber-600'}`}>
              Total contribución: {totalContribution}%
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {populatedCollaborators.map((collab) => (
              <div key={collab.organization_id} className="flex items-center gap-3 px-4 py-3">
                <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <UserGroupIcon className="h-5 w-5 text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{collab.orgName || collab.label}</p>
                  <p className="text-xs text-gray-500">
                    {collab.role === 'owner' ? 'Propietario' : 'Editor'} · {collab.contribution}%
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {/* Role */}
                  <select
                    value={collab.role}
                    onChange={(e) => updateCollaboratorRole(collab.organization_id, e.target.value as 'owner' | 'editor')}
                    className="px-2 py-1 text-xs border border-gray-300 rounded"
                  >
                    <option value="owner">Propietario</option>
                    <option value="editor">Editor</option>
                  </select>

                  {/* Contribution % */}
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={collab.contribution}
                      onChange={(e) => updateContribution(collab.organization_id, Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                    />
                    <span className="text-xs text-gray-500">%</span>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => removeCollaborator(collab.organization_id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validation messages */}
      {totalContribution !== 100 && populatedCollaborators.length > 0 && (
        <p className="text-sm text-amber-600">
          Las contribuciones deben sumar 100%. Actualmente: {totalContribution}%.
        </p>
      )}
      {populatedCollaborators.length < form.collaborativeSettings.minCollaborators && (
        <p className="text-sm text-amber-600">
          Necesitas al menos {form.collaborativeSettings.minCollaborators} colaboradores.
        </p>
      )}

      {/* Tejedores colaboradores de esta org (fichas F3) — la vía directa (F5) */}
      {weaverOptions.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-1">Tus tejedores colaboradores</h4>
          <p className="text-xs text-gray-500 mb-2">
            Colaboradores con los que ya trabajas. Al añadir uno, sus productos ofrecidos aparecen
            en el paso siguiente.
          </p>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {weaverOptions.map((w) => {
              const added = form.collaborativeSettings.collaborators.some(
                (c) => c.organization_id === w.weaver_org_id
              );
              return (
                <div key={w.weaver_org_id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50">
                  <div className="h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center">
                    <UserGroupIcon className="h-4 w-4 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{w.alias || w.name}</p>
                    <p className="text-xs text-gray-500">
                      {w.products.length} producto{w.products.length !== 1 ? 's' : ''} ofrecido{w.products.length !== 1 ? 's' : ''} a la red
                    </p>
                  </div>
                  <button
                    onClick={() => addCollaborator({ _id: w.weaver_org_id, name: w.name, slug: w.slug })}
                    disabled={added || form.collaborativeSettings.collaborators.length >= form.collaborativeSettings.maxCollaborators}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-teal-600 border border-teal-300 rounded-lg hover:bg-teal-50 disabled:opacity-50"
                  >
                    <PlusIcon className="h-3 w-3" />
                    {added ? 'Añadido' : 'Añadir'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search & add organizations */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Buscar organizaciones</h4>
        <div className="relative mb-3">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={orgSearch}
            onChange={(e) => setOrgSearch(e.target.value)}
            placeholder="Buscar por nombre..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-vida-500"
          />
        </div>

        {searchingOrgs ? (
          <p className="text-sm text-gray-400 py-4 text-center">Buscando...</p>
        ) : orgSearchResults.length > 0 ? (
          <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
            {orgSearchResults
              .filter((org) => !form.collaborativeSettings.collaborators.some((c) => c.organization_id === org._id))
              .map((org) => (
                <div
                  key={org._id}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50"
                >
                  <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <UserGroupIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{org.name}</p>
                    <p className="text-xs text-gray-500">{org.slug}</p>
                  </div>
                  <button
                    onClick={() => addCollaborator(org)}
                    disabled={form.collaborativeSettings.collaborators.length >= form.collaborativeSettings.maxCollaborators}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-teal-600 border border-teal-300 rounded-lg hover:bg-teal-50 disabled:opacity-50"
                  >
                    <PlusIcon className="h-3 w-3" />
                    Añadir
                  </button>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-4 text-center">
            {orgSearch ? 'No se encontraron organizaciones' : 'Escribe para buscar organizaciones'}
          </p>
        )}
      </div>
    </div>
  );

  // --- Collaborative: Contributor Products Step ---

  const renderStepContributorProducts = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Productos por Colaborador</h3>
      <p className="text-sm text-gray-500">
        Asigna productos simples a cada colaborador. Cada organización contribuye con sus propios productos.
      </p>

      {form.collaborativeSettings.collaborators.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <UserGroupIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>Primero selecciona colaboradores en el paso anterior</p>
        </div>
      ) : (
        form.collaborativeSettings.collaborators.map((collab) => {
          const collabProducts = populatedContributorProducts.filter(
            (cp) => cp.organization_id === collab.organization_id
          );
          return (
            <div key={collab.organization_id} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-teal-50 px-4 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="h-4 w-4 text-teal-600" />
                  <h4 className="text-sm font-semibold text-teal-800">{collab.label}</h4>
                  <span className="text-xs text-teal-600">({collab.contribution}%)</span>
                </div>
                <span className="text-xs text-gray-500">
                  {collabProducts.length} producto{collabProducts.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Products assigned to this collaborator */}
              {collabProducts.length > 0 && (
                <div className="divide-y divide-gray-100">
                  {collabProducts.map((cp) => (
                    <div key={`${cp.product_id}-${cp.organization_id}`} className="flex items-center gap-3 px-4 py-2.5">
                      {cp.source?.mainImage ? (
                        <img src={cp.source.mainImage} alt={cp.label} className="h-8 w-8 rounded object-cover" />
                      ) : (
                        <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                          <CubeIcon className="h-4 w-4 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{cp.label || cp.source?.name}</p>
                        <p className="text-xs text-gray-500">
                          {cp.source?.price?.toFixed(2)} € · x{cp.quantity}
                        </p>
                      </div>
                      <button
                        onClick={() => removeContributorProduct(cp.product_id, cp.organization_id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Productos que el tejedor OFRECE a la red (F5): quick-add directo */}
              {(() => {
                const wOpt = weaverOptions.find((w) => w.weaver_org_id === collab.organization_id);
                const existingIds = collabProducts.map((cp) => cp.product_id);
                const available = (wOpt?.products || []).filter((p) => !existingIds.includes(p._id));
                if (available.length === 0) return null;
                return (
                  <div className="px-3 pt-3 bg-gray-50 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-600 mb-2">Productos que ofrece a la red</p>
                    <div className="flex flex-wrap gap-2">
                      {available.map((p) => (
                        <button
                          key={p._id}
                          onClick={() =>
                            addContributorProduct(
                              {
                                _id: p._id,
                                name: p.name,
                                price: p.price ?? 0,
                                pvpFinal: p.price ?? 0,
                                sku: p.sku || '',
                                mainImage: p.mainImage || '',
                                status: 'active',
                                tipoProducto: '',
                              },
                              collab.organization_id
                            )
                          }
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-teal-300 rounded-lg text-teal-700 hover:bg-teal-50"
                        >
                          <PlusIcon className="h-3 w-3" />
                          {p.name}
                          {typeof p.price === 'number' ? ` · ${p.price.toFixed(2)} €` : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Inline search to add products for this collaborator */}
              <div className="p-3 border-t border-gray-100 bg-gray-50">
                <CollaboratorProductSearch
                  orgId={collab.organization_id}
                  existingProductIds={collabProducts.map((cp) => cp.product_id)}
                  onAdd={(product) => addContributorProduct(product, collab.organization_id)}
                />
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  const renderStep1_Categorization = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Categorización</h3>
      <p className="text-sm text-gray-500">
        Selecciona nodos de los árboles de metadatos para categorizar el producto.
      </p>

      {metadataTrees.length === 0 && (
        <p className="text-xs text-gray-400 italic">No hay árboles de metadatos disponibles.</p>
      )}

      {metadataTrees.map((matchingTree) => {
        // Key = tree id (slug). Holds both global VIDA trees and org-scoped ones
        // (e.g. alicun-comercial), served already scoped by GET /metadata/trees.
        const key = matchingTree.id;
        const label = matchingTree.name;
        const allNodes = treeNodes[matchingTree._id] || [];
        // Filter nodes by project scope for external users
        const nodes = allowedNodeIds
          ? allNodes.filter((n) => allowedNodeIds.has(n._id))
          : allNodes;
        const selectedIds = Array.isArray(form.categorization[key]) ? form.categorization[key] : [];

        return (
          <div key={matchingTree._id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700">{label}</h4>
              {!treeNodes[matchingTree._id] && (
                <button
                  onClick={() => loadNodesForTree(matchingTree._id)}
                  className="text-xs text-vida-600 hover:underline"
                >
                  Cargar nodos
                </button>
              )}
              <span className="text-xs text-gray-400">
                {selectedIds.length} seleccionado{selectedIds.length !== 1 ? 's' : ''}
              </span>
            </div>

            {nodes.length === 0 ? (
              <p className="text-xs text-gray-400 italic">
                {treeNodes[matchingTree._id] ? 'Sin nodos disponibles' : 'Cargando nodos…'}
              </p>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-1">
                {nodes.map((node) => (
                  <label
                    key={node._id}
                    className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded cursor-pointer"
                    style={{ paddingLeft: `${(node.depth || 0) * 16 + 8}px` }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(node._id)}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...selectedIds, node._id]
                          : selectedIds.filter((id) => id !== node._id);
                        updateField('categorization', {
                          ...form.categorization,
                          [key]: updated,
                        });
                      }}
                      className="rounded border-gray-300 text-vida-600"
                    />
                    <span className="text-sm text-gray-700">{node.name}</span>
                    <span className="text-xs text-gray-400">{node.path}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Manual tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Etiquetas adicionales</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {form.tags.map((tag, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2 py-1 bg-vida-100 text-vida-700 rounded-full text-xs"
            >
              {tag}
              <button
                onClick={() =>
                  updateField(
                    'tags',
                    form.tags.filter((_, idx) => idx !== i)
                  )
                }
                className="hover:text-red-500"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <input
          type="text"
          placeholder="Escribe y pulsa Enter para añadir"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const val = (e.target as HTMLInputElement).value.trim();
              if (val && !form.tags.includes(val)) {
                updateField('tags', [...form.tags, val]);
                (e.target as HTMLInputElement).value = '';
              }
            }
          }}
        />
      </div>
    </div>
  );

  const renderStep2_Marketing = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Marketing</h3>
        <AiGenerateButton
          task="marketing-pack"
          label="Generar marketing con IA"
          getContext={() => `${form.name}\n\n${form.description}`}
          onResult={(text) => {
            // The model returns a labelled block (ARGUMENTO:, DESCRIPCION:, ...).
            // Parse line by line and fill every marketing field at once. Fields
            // de segmentación del producto (clienteObjetivo) se dejan intactos.
            const pick = (label: string): string => {
              const m = text.match(new RegExp(`^${label}:\\s*(.+)$`, 'im'));
              return m ? m[1].trim() : '';
            };
            const argumento = pick('ARGUMENTO');
            const descripcion = pick('DESCRIPCION');
            const ogTitle = pick('OG_TITULO');
            const ogDescription = pick('OG_DESCRIPCION');
            const metaDescription = pick('META_DESCRIPCION');
            const keywords = pick('KEYWORDS');
            const targetAudience = pick('PUBLICO');
            const callToAction = pick('CTA');
            updateField('marketing', {
              ...form.marketing,
              argumentoVenta: argumento || form.marketing.argumentoVenta,
              descripcionMarketing: descripcion || form.marketing.descripcionMarketing,
              metaSeo: {
                ...form.marketing.metaSeo,
                ogTitle: ogTitle || form.marketing.metaSeo.ogTitle,
                ogDescription: ogDescription || form.marketing.metaSeo.ogDescription,
                metaDescription: metaDescription || form.marketing.metaSeo.metaDescription,
                keywords: keywords || form.marketing.metaSeo.keywords,
                targetAudience: targetAudience || form.marketing.metaSeo.targetAudience,
                callToAction: callToAction || form.marketing.metaSeo.callToAction,
              },
            });
          }}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Argumento de Venta</label>
        <textarea
          value={form.marketing.argumentoVenta}
          onChange={(e) =>
            updateField('marketing', { ...form.marketing, argumentoVenta: e.target.value })
          }
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Descripción Marketing</label>
        <textarea
          value={form.marketing.descripcionMarketing}
          onChange={(e) =>
            updateField('marketing', { ...form.marketing, descripcionMarketing: e.target.value })
          }
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        />
      </div>

      <details className="border border-gray-200 rounded-lg p-4">
        <summary className="cursor-pointer text-sm font-semibold text-gray-700">
          Meta SEO
          <span className="ml-2 text-xs font-normal text-gray-400">
            (se rellena con el botón "Generar marketing con IA" de arriba)
          </span>
        </summary>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {['ogTitle', 'ogDescription', 'metaDescription', 'keywords', 'targetAudience', 'callToAction'].map(
            (field) => (
              <div key={field}>
                <label className="block text-xs text-gray-500 mb-1 capitalize">
                  {field.replace(/([A-Z])/g, ' $1')}
                </label>
                <input
                  type="text"
                  value={(form.marketing.metaSeo as Record<string, string>)[field] || ''}
                  onChange={(e) =>
                    updateField('marketing', {
                      ...form.marketing,
                      metaSeo: { ...form.marketing.metaSeo, [field]: e.target.value },
                    })
                  }
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
              </div>
            )
          )}
        </div>
      </details>

      {/* Cliente Objetivo */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cliente Objetivo</label>
        <input
          type="text"
          placeholder="Tipos de cliente (separados por coma)"
          value={form.clienteObjetivo.tipoCliente.join(', ')}
          onChange={(e) =>
            updateField('clienteObjetivo', {
              ...form.clienteObjetivo,
              tipoCliente: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
            })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>

      {/* Estructura Producto */}
      <details className="border border-gray-200 rounded-lg p-4">
        <summary className="cursor-pointer text-sm font-semibold text-gray-700">
          Estructura de Producto
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {['perspectivaCostes', 'margenComercial', 'margenIndustrial', 'iddi'].map((field) => (
            <div key={field}>
              <label className="block text-xs text-gray-500 mb-1 capitalize">
                {field.replace(/([A-Z])/g, ' $1')}
              </label>
              <input
                type="text"
                value={(form.estructuraProducto as Record<string, string>)[field] || ''}
                onChange={(e) =>
                  updateField('estructuraProducto', {
                    ...form.estructuraProducto,
                    [field]: e.target.value,
                  })
                }
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
          ))}
        </div>
      </details>
    </div>
  );

  const renderStep3_Otros = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Otros</h3>

      {/* Aspectos Generales */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">Aspectos Generales</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Temporada</label>
            <select
              value={form.aspectosGenerales.tipoTemporada}
              onChange={(e) =>
                updateField('aspectosGenerales', {
                  ...form.aspectosGenerales,
                  tipoTemporada: e.target.value,
                })
              }
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            >
              <option value="">—</option>
              <option value="siempre">Todo el año</option>
              <option value="temporada">Por temporada</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Duración (días)</label>
            <input
              type="number"
              min={0}
              value={form.aspectosGenerales.duracionDias}
              onChange={(e) =>
                updateField('aspectosGenerales', {
                  ...form.aspectosGenerales,
                  duracionDias: parseInt(e.target.value) || 0,
                })
              }
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Pernoctas</label>
            <input
              type="number"
              min={0}
              value={form.aspectosGenerales.pernoctas}
              onChange={(e) =>
                updateField('aspectosGenerales', {
                  ...form.aspectosGenerales,
                  pernoctas: parseInt(e.target.value) || 0,
                })
              }
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.aspectosGenerales.diaEntrada}
                onChange={(e) =>
                  updateField('aspectosGenerales', {
                    ...form.aspectosGenerales,
                    diaEntrada: e.target.checked,
                  })
                }
                className="rounded border-gray-300"
              />
              Día de entrada
            </label>
          </div>
        </div>
      </div>

      {/* Disponibilidad */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">Disponibilidad del Negocio</h4>
        <select
          value={form.disponibilidadNegocio.tipoApertura}
          onChange={(e) =>
            updateField('disponibilidadNegocio', {
              ...form.disponibilidadNegocio,
              tipoApertura: e.target.value,
            })
          }
          className="px-2 py-1.5 border border-gray-300 rounded text-sm"
        >
          <option value="todo-año">Todo el año</option>
          <option value="temporadas">Por temporadas</option>
          <option value="personalizado">Personalizado</option>
        </select>

        <div>
          <p className="text-xs text-gray-500 mb-1">Días de la semana</p>
          <div className="flex flex-wrap gap-2">
            {DIAS_SEMANA.map((dia) => (
              <label key={dia} className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={form.disponibilidadNegocio.diasSemana[dia] ?? true}
                  onChange={(e) =>
                    updateField('disponibilidadNegocio', {
                      ...form.disponibilidadNegocio,
                      diasSemana: { ...form.disponibilidadNegocio.diasSemana, [dia]: e.target.checked },
                    })
                  }
                  className="rounded border-gray-300"
                />
                <span className="capitalize">{dia}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Running */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <input
            type="checkbox"
            checked={form.running.activo}
            onChange={(e) =>
              updateField('running', { ...form.running, activo: e.target.checked })
            }
            className="rounded border-gray-300"
          />
          Running (Actividades)
        </label>
        {form.running.activo && (
          <textarea
            value={form.running.descripcionActividad}
            onChange={(e) =>
              updateField('running', { ...form.running, descripcionActividad: e.target.value })
            }
            rows={2}
            placeholder="Descripción de actividades"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        )}
      </div>

      {/* Gastro */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <input
            type="checkbox"
            checked={form.gastro.activo}
            onChange={(e) =>
              updateField('gastro', { ...form.gastro, activo: e.target.checked })
            }
            className="rounded border-gray-300"
          />
          Gastronomía
        </label>
        {form.gastro.activo && (
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={form.gastro.tipoServicio}
              onChange={(e) =>
                updateField('gastro', { ...form.gastro, tipoServicio: e.target.value })
              }
              placeholder="Tipo de servicio"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              value={form.gastro.logistica}
              onChange={(e) =>
                updateField('gastro', { ...form.gastro, logistica: e.target.value })
              }
              placeholder="Logística"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        )}
      </div>

      {/* Cupo */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <input
            type="checkbox"
            checked={form.cupo.activo}
            onChange={(e) =>
              updateField('cupo', { ...form.cupo, activo: e.target.checked })
            }
            className="rounded border-gray-300"
          />
          Cupo
        </label>
        {form.cupo.activo && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <select
              value={form.cupo.tipoLimite}
              onChange={(e) =>
                updateField('cupo', { ...form.cupo, tipoLimite: e.target.value })
              }
              className="px-2 py-1.5 border border-gray-300 rounded text-sm"
            >
              <option value="cantidad">Por cantidad</option>
              <option value="fecha">Por fecha</option>
              <option value="ambos">Ambos</option>
            </select>
            <input
              type="number"
              min={0}
              value={form.cupo.cantidadMaxima}
              onChange={(e) =>
                updateField('cupo', { ...form.cupo, cantidadMaxima: parseInt(e.target.value) || 0 })
              }
              placeholder="Cantidad máxima"
              className="px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
            <input
              type="date"
              value={form.cupo.fechaLimite}
              onChange={(e) =>
                updateField('cupo', { ...form.cupo, fechaLimite: e.target.value })
              }
              className="px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderStep4_Inventario = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Inventario</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Stock actual</label>
          <input
            type="number"
            min={0}
            value={form.stock}
            onChange={(e) => updateField('stock', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Stock mínimo</label>
          <input
            type="number"
            min={0}
            value={form.stockMinimo}
            onChange={(e) => updateField('stockMinimo', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm pb-2">
            <input
              type="checkbox"
              checked={form.alertaStock}
              onChange={(e) => updateField('alertaStock', e.target.checked)}
              className="rounded border-gray-300 text-vida-600"
            />
            Alerta de stock bajo
          </label>
        </div>
      </div>
    </div>
  );

  const renderStep5_Variants = () => {
    const variants = form.variants;
    const updateVariant = (idx: number, patch: Partial<VariantFormData>) => {
      setForm((prev) => ({
        ...prev,
        variants: prev.variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)),
      }));
    };
    const addVariant = () => {
      setForm((prev) => ({ ...prev, variants: [...prev.variants, makeEmptyVariant()] }));
    };
    const removeVariant = (idx: number) => {
      setForm((prev) => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
    };
    const duplicateVariant = (idx: number) => {
      setForm((prev) => {
        const src = prev.variants[idx];
        if (!src) return prev;
        const copy: VariantFormData = {
          ...src,
          _id: undefined,
          sku: src.sku ? `${src.sku}-COPY` : '',
          attributes: { ...src.attributes },
          images: [...src.images],
        };
        return { ...prev, variants: [...prev.variants.slice(0, idx + 1), copy, ...prev.variants.slice(idx + 1)] };
      });
    };
    const updateAttribute = (idx: number, oldKey: string, newKey: string, value: string) => {
      const variant = variants[idx];
      const next: Record<string, string> = {};
      Object.entries(variant.attributes).forEach(([k, v]) => {
        if (k === oldKey) {
          if (newKey.trim()) next[newKey] = value;
        } else {
          next[k] = v;
        }
      });
      updateVariant(idx, { attributes: next });
    };
    const addAttribute = (idx: number) => {
      const variant = variants[idx];
      let key = 'Atributo';
      let n = 1;
      while (variant.attributes[key]) {
        n += 1;
        key = `Atributo ${n}`;
      }
      updateVariant(idx, { attributes: { ...variant.attributes, [key]: '' } });
    };
    const removeAttribute = (idx: number, key: string) => {
      const variant = variants[idx];
      const next = { ...variant.attributes };
      delete next[key];
      updateVariant(idx, { attributes: next });
    };

    // Surface duplicate-SKU collisions live so the user fixes them before save.
    const skuCounts = new Map<string, number>();
    variants.forEach((v) => {
      const s = v.sku.trim().toUpperCase();
      if (!s) return;
      skuCounts.set(s, (skuCounts.get(s) || 0) + 1);
    });

    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">Variantes</h3>
            <p className="text-sm text-gray-500 mt-1">
              Opcional. Si tu producto tiene varias combinaciones (talla, color, sabor, volumen, idioma…),
              añade una variante por cada SKU comprable. Sin variantes el producto se vende como SKU único.
            </p>
          </div>
          <button
            type="button"
            onClick={addVariant}
            className="shrink-0 px-4 py-2 bg-vida-600 text-white rounded-lg hover:bg-vida-700 text-sm font-medium"
          >
            + Añadir variante
          </button>
        </div>

        {variants.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <p className="text-sm text-gray-500">
              Sin variantes. El producto se vende con el SKU principal definido en Inventario.
            </p>
          </div>
        )}

        {variants.map((variant, idx) => {
          const skuKey = variant.sku.trim().toUpperCase();
          const isDuplicate = !!skuKey && (skuCounts.get(skuKey) || 0) > 1;
          return (
            <div
              key={variant._id || `new-${idx}`}
              className={`border rounded-lg p-4 space-y-4 ${
                variant.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Variante {idx + 1}</span>
                  {!variant.active && (
                    <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">Inactiva</span>
                  )}
                  {isDuplicate && (
                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">SKU duplicado</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 mr-2">
                    <input
                      type="checkbox"
                      checked={variant.active}
                      onChange={(e) => updateVariant(idx, { active: e.target.checked })}
                    />
                    Activa
                  </label>
                  <button
                    type="button"
                    onClick={() => duplicateVariant(idx)}
                    className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                  >
                    Duplicar
                  </button>
                  <button
                    type="button"
                    onClick={() => removeVariant(idx)}
                    className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                  >
                    Borrar
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">SKU *</label>
                  <input
                    type="text"
                    value={variant.sku}
                    onChange={(e) => updateVariant(idx, { sku: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${
                      isDuplicate ? 'border-red-400' : 'border-gray-300'
                    }`}
                    placeholder="VINO-RES-2020-75"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nombre visible</label>
                  <input
                    type="text"
                    value={variant.name}
                    onChange={(e) => updateVariant(idx, { name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Reserva 2020 — 75cl"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-gray-700">Atributos</label>
                  <button
                    type="button"
                    onClick={() => addAttribute(idx)}
                    className="text-xs text-vida-600 hover:text-vida-700"
                  >
                    + Añadir atributo
                  </button>
                </div>
                {Object.keys(variant.attributes).length === 0 ? (
                  <p className="text-xs text-gray-400 italic">
                    Sin atributos. Añade los ejes que describan esta variante (ej. Talla, Color, Volumen, Sabor, Duración…).
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {Object.entries(variant.attributes).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={key}
                          onChange={(e) => updateAttribute(idx, key, e.target.value, value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs"
                          placeholder="Nombre del atributo"
                        />
                        <span className="text-gray-400 text-xs">=</span>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => updateAttribute(idx, key, key, e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs"
                          placeholder="Valor"
                        />
                        <button
                          type="button"
                          onClick={() => removeAttribute(idx, key)}
                          className="px-2 py-1 text-xs text-red-500 hover:bg-red-50 rounded"
                          aria-label="Quitar atributo"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Precio
                    <span className="block text-[10px] text-gray-400">0 = hereda producto</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={variant.price}
                    onChange={(e) => updateVariant(idx, { price: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Antes
                    <span className="block text-[10px] text-gray-400">0 = sin tachado</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={variant.compareAtPrice}
                    onChange={(e) => updateVariant(idx, { compareAtPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Stock</label>
                  <input
                    type="number"
                    min={0}
                    value={variant.stock}
                    onChange={(e) => updateVariant(idx, { stock: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Stock mín.</label>
                  <input
                    type="number"
                    min={0}
                    value={variant.stockMinimo}
                    onChange={(e) => updateVariant(idx, { stockMinimo: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Código de barras</label>
                  <input
                    type="text"
                    value={variant.barcode}
                    onChange={(e) => updateVariant(idx, { barcode: e.target.value })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                    placeholder="EAN, UPC…"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Peso (gramos)</label>
                  <input
                    type="number"
                    min={0}
                    value={variant.weight}
                    onChange={(e) => updateVariant(idx, { weight: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  URLs de imágenes específicas
                  <span className="block text-[10px] text-gray-400">Una por línea. Vacío = usa imágenes del producto.</span>
                </label>
                <textarea
                  value={variant.images.join('\n')}
                  onChange={(e) =>
                    updateVariant(idx, {
                      images: e.target.value
                        .split('\n')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  rows={2}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-mono text-xs"
                  placeholder="https://storage.livre.es/vida-org/products/..."
                />
              </div>
            </div>
          );
        })}

        {variants.length > 0 && (
          <button
            type="button"
            onClick={addVariant}
            className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-500 rounded-lg hover:border-vida-400 hover:text-vida-600 text-sm"
          >
            + Añadir otra variante
          </button>
        )}
      </div>
    );
  };

  const renderStep5_Images = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Imágenes</h3>

      {/* Upload area */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <CloudArrowUpIcon className="h-10 w-10 mx-auto text-gray-400 mb-3" />
        <p className="text-sm text-gray-500 mb-2">Arrastra imágenes o haz clic para subir</p>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
          className="hidden"
          id="image-upload"
        />
        <label
          htmlFor="image-upload"
          className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white rounded-lg hover:bg-vida-700 cursor-pointer text-sm"
        >
          <PhotoIcon className="h-4 w-4" />
          Seleccionar archivos
        </label>
        {!productId && (
          <p className="text-xs text-yellow-600 mt-2">
            El producto se guardará automáticamente al subir la primera imagen
          </p>
        )}
      </div>

      {/* Image grid */}
      {form.images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {form.images.map((img) => (
            <div
              key={img.id}
              className={`relative border-2 rounded-lg overflow-hidden ${
                form.mainImage === img.url ? 'border-vida-500' : 'border-gray-200'
              }`}
            >
              <img src={img.url} alt={img.etiqueta || 'Product'} className="w-full h-32 object-cover" />
              <div className="absolute top-1 right-1 flex gap-1">
                <button
                  onClick={() => setMainImage(img.url)}
                  className={`p-1 rounded ${
                    form.mainImage === img.url ? 'bg-yellow-400 text-white' : 'bg-white/80 text-gray-500 hover:text-yellow-500'
                  }`}
                  title="Imagen principal"
                >
                  {form.mainImage === img.url ? (
                    <StarIconSolid className="h-4 w-4" />
                  ) : (
                    <StarIcon className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => handleDeleteImage(img.id)}
                  className="p-1 bg-white/80 text-red-500 rounded hover:bg-red-100"
                  title="Eliminar"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="p-2">
                <input
                  type="text"
                  value={img.etiqueta}
                  onChange={(e) => {
                    const updated = form.images.map((i) =>
                      i.id === img.id ? { ...i, etiqueta: e.target.value } : i
                    );
                    updateField('images', updated);
                  }}
                  placeholder="Etiqueta"
                  className="w-full text-xs px-1 py-0.5 border border-gray-200 rounded"
                />
                <select
                  value={img.temporada}
                  onChange={(e) => {
                    const updated = form.images.map((i) =>
                      i.id === img.id ? { ...i, temporada: e.target.value as ImageWithMetadata['temporada'] } : i
                    );
                    updateField('images', updated);
                  }}
                  className="w-full text-xs px-1 py-0.5 border border-gray-200 rounded mt-1"
                >
                  <option value="general">General</option>
                  <option value="primavera">Primavera</option>
                  <option value="verano">Verano</option>
                  <option value="otoño">Otoño</option>
                  <option value="invierno">Invierno</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep6_Enlaces = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Enlaces y Activación</h3>

      {/* Activation toggles */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">Canales de Activación</h4>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.ecommerce_active}
            onChange={(e) => updateField('ecommerce_active', e.target.checked)}
            className="rounded border-gray-300 text-vida-600 h-5 w-5"
          />
          <div>
            <span className="text-sm font-medium">Ecommerce</span>
            <p className="text-xs text-gray-500">Visible en la tienda online</p>
          </div>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.catalog_active}
            onChange={(e) => updateField('catalog_active', e.target.checked)}
            className="rounded border-gray-300 text-purple-600 h-5 w-5"
          />
          <div>
            <span className="text-sm font-medium">Catálogo</span>
            <p className="text-xs text-gray-500">Visible en el catálogo público</p>
          </div>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.shared_with_network}
            onChange={(e) => updateField('shared_with_network', e.target.checked)}
            className="rounded border-gray-300 text-teal-600 h-5 w-5"
          />
          <div>
            <span className="text-sm font-medium">Ofrecer a la red</span>
            <p className="text-xs text-gray-500">
              Disponible como opción en los productos colaborativos de las organizaciones con las que colaboras (Tejedores)
            </p>
          </div>
        </label>

        {/* Landing de producto */}
        <div className="pt-1">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.landing.active}
              onChange={(e) => updateField('landing', { ...form.landing, active: e.target.checked })}
              className="rounded border-gray-300 text-pink-600 h-5 w-5"
            />
            <div>
              <span className="text-sm font-medium">Landing de producto</span>
              <p className="text-xs text-gray-500">Página de aterrizaje individual (límite según tu plan)</p>
            </div>
          </label>

          {form.landing.active && (
            <div className="mt-3 ml-8 space-y-3 border-l-2 border-pink-100 pl-4">
              {/* Datos que hace falta conocer para desplegar la landing en Vercel.
                  Sin esto el usuario activa la landing y no sabe qué slug usar. */}
              <div className="rounded-lg border border-pink-200 bg-pink-50 p-3">
                <p className="text-xs font-semibold text-pink-900 mb-2">
                  Datos para desplegar esta landing
                </p>
                {product?.slug ? (
                  <div className="space-y-1.5">
                    {orgSlug && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-44 shrink-0">NEXT_PUBLIC_ORG_SLUG</span>
                        <code className="flex-1 rounded bg-white px-2 py-1 font-mono text-xs text-gray-800 border border-pink-100">
                          {orgSlug}
                        </code>
                        <button
                          type="button"
                          onClick={() => copyValue('org', orgSlug)}
                          className="text-xs text-pink-700 hover:underline shrink-0"
                        >
                          {copiedField === 'org' ? 'Copiado' : 'Copiar'}
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-44 shrink-0">NEXT_PUBLIC_PRODUCT_SLUG</span>
                      <code className="flex-1 rounded bg-white px-2 py-1 font-mono text-xs text-gray-800 border border-pink-100">
                        {product.slug}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyValue('slug', product.slug)}
                        className="text-xs text-pink-700 hover:underline shrink-0"
                      >
                        {copiedField === 'slug' ? 'Copiado' : 'Copiar'}
                      </button>
                    </div>
                    <p className="pt-1 text-xs text-gray-500">
                      Son las dos variables de entorno del proyecto de Vercel. Al cambiarlas hay que
                      <strong> redesplegar</strong>: se hornean en el build.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-600">
                    El slug se genera al guardar el producto. Guarda y vuelve a esta pantalla para verlo.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de plantilla</label>
                  <select
                    value={form.landing.template}
                    onChange={(e) => {
                      const template = e.target.value as LandingTemplate;
                      // Reset the style to the first valid one for the new type.
                      const styles = LANDING_STYLES[template];
                      const style = styles.some((s) => s.id === form.landing.style) ? form.landing.style : styles[0].id;
                      updateField('landing', { ...form.landing, template, style });
                    }}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  >
                    {LANDING_TEMPLATES.map((t) => (
                      <option key={t} value={t}>{LANDING_TEMPLATE_LABELS[t]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Estilo</label>
                  <select
                    value={form.landing.style || LANDING_STYLES[form.landing.template][0].id}
                    onChange={(e) => updateField('landing', { ...form.landing, style: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    disabled={LANDING_STYLES[form.landing.template].length < 2}
                  >
                    {LANDING_STYLES[form.landing.template].map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Etiqueta / Badge</label>
                <input
                  type="text"
                  value={form.landing.badge}
                  onChange={(e) => updateField('landing', { ...form.landing, badge: e.target.value })}
                  placeholder="p. ej. 30 SOFTGELS, NOVEDAD, ECO..."
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
              </div>

              {/* Bloques de información: el usuario define etiqueta + contenido */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-600">Bloques de información</label>
                  <button
                    type="button"
                    onClick={() => updateField('landing', { ...form.landing, info_blocks: [...form.landing.info_blocks, { label: '', body: '' }] })}
                    className="flex items-center gap-1 text-xs text-pink-600 hover:underline"
                  >
                    <PlusIcon className="h-3.5 w-3.5" /> Añadir bloque
                  </button>
                </div>
                <p className="text-xs text-gray-400 mb-2">Secciones con título propio (p. ej. &quot;Cómo usar&quot;, &quot;Qué incluye&quot;, &quot;Duración&quot;). Añade solo las que apliquen a este producto.</p>
                {form.landing.info_blocks.length === 0 && (
                  <p className="text-xs text-gray-400 italic">Sin bloques. Pulsa &quot;Añadir bloque&quot; para crear uno.</p>
                )}
                <div className="space-y-2">
                  {form.landing.info_blocks.map((block, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-2 bg-white">
                      <div className="flex items-center gap-2 mb-1">
                        <input
                          type="text"
                          value={block.label}
                          onChange={(e) => {
                            const next = [...form.landing.info_blocks];
                            next[idx] = { ...next[idx], label: e.target.value };
                            updateField('landing', { ...form.landing, info_blocks: next });
                          }}
                          placeholder="Título del bloque (p. ej. Cómo usar)"
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-medium"
                        />
                        <button
                          type="button"
                          onClick={() => updateField('landing', { ...form.landing, info_blocks: form.landing.info_blocks.filter((_, i) => i !== idx) })}
                          className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <textarea
                        value={block.body}
                        onChange={(e) => {
                          const next = [...form.landing.info_blocks];
                          next[idx] = { ...next[idx], body: e.target.value };
                          updateField('landing', { ...form.landing, info_blocks: next });
                        }}
                        rows={2}
                        placeholder="Contenido del bloque"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enlaces */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700">Enlaces Externos</h4>
          <button
            onClick={addEnlace}
            className="flex items-center gap-1 text-xs text-vida-600 hover:underline"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Añadir enlace
          </button>
        </div>
        {form.enlaces.map((enlace) => (
          <div key={enlace.id} className="flex items-center gap-2 border border-gray-200 rounded-lg p-2">
            <select
              value={enlace.tipo}
              onChange={(e) => updateEnlace(enlace.id, 'tipo', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-sm"
            >
              <option value="tienda">Tienda</option>
              <option value="catalogo">Catálogo</option>
              <option value="landing">Landing</option>
            </select>
            <input
              type="url"
              value={enlace.url}
              onChange={(e) => updateEnlace(enlace.id, 'url', e.target.value)}
              placeholder="https://..."
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={enlace.activo}
                onChange={(e) => updateEnlace(enlace.id, 'activo', e.target.checked)}
                className="rounded border-gray-300"
              />
              Activo
            </label>
            <button
              onClick={() => removeEnlace(enlace.id)}
              className="p-1 text-red-500 hover:bg-red-50 rounded"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep7_Resumen = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Resumen</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-700">Información General</h4>
          <p className="text-sm"><b>Nombre:</b> {form.name || '—'}</p>
          <p className="text-sm"><b>Tipo:</b> {form.type === 'composite' ? 'Compuesto' : form.type === 'collaborative' ? 'Colaborativo' : 'Simple'}</p>
          <p className="text-sm"><b>Clasificación:</b> {form.tipoProducto ? TIPO_PRODUCTO_LABELS[form.tipoProducto] : '—'}</p>
          <p className="text-sm"><b>SKU:</b> {form.sku || '—'}</p>
          {/* El slug es la identidad pública del producto: aparece en su URL y es
              lo que necesitan el catálogo, la tienda y la landing. */}
          <p className="text-sm">
            <b>Slug:</b>{' '}
            {product?.slug
              ? <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200">{product.slug}</code>
              : <span className="text-gray-400">se generará al guardar</span>}
          </p>
          {isComposite && (
            <p className="text-sm"><b>Componentes:</b> {form.components.length}</p>
          )}
          {isCollaborative && (
            <>
              <p className="text-sm"><b>Colaboradores:</b> {form.collaborativeSettings.collaborators.length}</p>
              <p className="text-sm"><b>Visibilidad:</b> {VISIBILITY_LABELS[form.collaborativeSettings.visibilityLevel]}</p>
            </>
          )}
        </div>

        {isComposite && populatedComponents.length > 0 && (
          <div className="bg-purple-50 rounded-lg p-4 space-y-2">
            <h4 className="text-sm font-semibold text-purple-700">Componentes</h4>
            {populatedComponents.map((c) => (
              <p key={c.product_id} className="text-sm">
                {c.label} x{c.quantity} = {c.subtotal.toFixed(2)} €
                {c.isOptional && <span className="text-yellow-600 ml-1">(opcional)</span>}
              </p>
            ))}
            <p className="text-sm font-semibold text-purple-800">
              Total componentes: {totalComponentsPrice.toFixed(2)} €
            </p>
          </div>
        )}

        {isCollaborative && populatedCollaborators.length > 0 && (
          <div className="bg-teal-50 rounded-lg p-4 space-y-2">
            <h4 className="text-sm font-semibold text-teal-700">Colaboradores</h4>
            {populatedCollaborators.map((c) => (
              <p key={c.organization_id} className="text-sm">
                {c.orgName || c.label} — {c.role === 'owner' ? 'Propietario' : 'Editor'} · {c.contribution}%
              </p>
            ))}
            <p className={`text-sm font-semibold ${totalContribution === 100 ? 'text-teal-800' : 'text-amber-600'}`}>
              Total contribución: {totalContribution}%
            </p>
            {populatedContributorProducts.length > 0 && (
              <>
                <h4 className="text-sm font-semibold text-teal-700 pt-2">Productos Contribuidos</h4>
                {populatedContributorProducts.map((cp) => (
                  <p key={`${cp.product_id}-${cp.organization_id}`} className="text-sm">
                    {cp.label || cp.source?.name} x{cp.quantity} ({cp.orgName})
                  </p>
                ))}
              </>
            )}
          </div>
        )}

        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-700">Precios</h4>
          <p className="text-sm"><b>Precio base:</b> {form.price.toFixed(2)} €</p>
          <p className="text-sm"><b>Descuento:</b> {form.descuento}%</p>
          <p className="text-sm"><b>IVA:</b> {form.iva}%</p>
          <p className="text-sm text-green-700 font-semibold"><b>PVP Final:</b> {form.pvpFinal.toFixed(2)} €</p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-700">Inventario</h4>
          <p className="text-sm"><b>Stock:</b> {form.stock}</p>
          <p className="text-sm"><b>Stock mínimo:</b> {form.stockMinimo}</p>
          <p className="text-sm"><b>Alerta:</b> {form.alertaStock ? 'Sí' : 'No'}</p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-700">Canales</h4>
          <p className="text-sm"><b>Ecommerce:</b> {form.ecommerce_active ? '✅ Activo' : '❌ Inactivo'}</p>
          <p className="text-sm"><b>Catálogo:</b> {form.catalog_active ? '✅ Activo' : '❌ Inactivo'}</p>
          <p className="text-sm"><b>Imágenes:</b> {form.images.length}</p>
          <p className="text-sm"><b>Enlaces:</b> {form.enlaces.length}</p>
        </div>

        {form.tags.length > 0 && (
          <div className="md:col-span-2 bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Etiquetas</h4>
            <div className="flex flex-wrap gap-2">
              {form.tags.map((tag, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 bg-vida-100 text-vida-700 rounded-full text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {form.description && (
          <div className="md:col-span-2 bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-1">Descripción</h4>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{form.description}</p>
          </div>
        )}
      </div>
    </div>
  );

  const stepRenderers = isCollaborative
    ? [
        renderStep0_InfoGeneral,
        renderStepCollaborators,
        renderStepContributorProducts,
        renderStep1_Categorization,
        renderStep2_Marketing,
        renderStep3_Otros,
        renderStep4_Inventario,
        renderStep5_Variants,
        renderStep5_Images,
        renderStep6_Enlaces,
        renderStep7_Resumen,
      ]
    : isComposite
      ? [
          renderStep0_InfoGeneral,
          renderStepComponents,
          renderStep1_Categorization,
          renderStep2_Marketing,
          renderStep3_Otros,
          renderStep4_Inventario,
          renderStep5_Variants,
          renderStep5_Images,
          renderStep6_Enlaces,
          renderStep7_Resumen,
        ]
      : [
          renderStep0_InfoGeneral,
          renderStep1_Categorization,
          renderStep2_Marketing,
          renderStep3_Otros,
          renderStep4_Inventario,
          renderStep5_Variants,
          renderStep5_Images,
          renderStep6_Enlaces,
          renderStep7_Resumen,
        ];

  // ---------------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onClose()}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {isEditing ? `Editar: ${product!.name}` : 'Nuevo Producto'}
            </h2>
            <p className="text-sm text-gray-500">{STEPS[activeStep].name}</p>
          </div>
        </div>
        <button
          onClick={() => onClose()}
          className="p-2 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {STEPS.map((step) => (
          <button
            key={step.id}
            onClick={() => goToStep(step.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              activeStep === step.id
                ? 'bg-vida-600 text-white'
                : activeStep > step.id
                  ? 'bg-vida-100 text-vida-700'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            <span
              className={`w-5 h-5 flex items-center justify-center rounded-full text-xs ${
                activeStep > step.id ? 'bg-vida-200 text-vida-700' : ''
              }`}
            >
              {activeStep > step.id ? <CheckIcon className="h-3 w-3" /> : step.icon}
            </span>
            {step.name}
          </button>
        ))}
      </div>

      {/* Step content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {stepRenderers[activeStep]()}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => goToStep(Math.max(0, activeStep - 1))}
          disabled={activeStep === 0}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Anterior
        </button>

        <div className="flex items-center gap-3">
          <button
            onClick={() => saveProduct(false)}
            disabled={saving || !form.name.trim()}
            className="px-4 py-2 text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar Borrador'}
          </button>

          {activeStep === STEPS.length - 1 ? (
            <button
              onClick={async () => {
                await saveProduct(true);
                onClose(true);
              }}
              disabled={saving || !form.name.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <CheckIcon className="h-4 w-4" />
              {saving ? 'Publicando...' : 'Publicar'}
            </button>
          ) : (
            <button
              onClick={() => goToStep(Math.min(STEPS.length - 1, activeStep + 1))}
              className="flex items-center gap-2 px-4 py-2 bg-vida-600 text-white rounded-lg hover:bg-vida-700"
            >
              Siguiente
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
